/**
 * The one Danish date/time format for the UI: "dd-mm-åååå tt:mm", always in
 * Europe/Copenhagen and independent of the browser locale (MedCom review
 * 03.11, G3).
 */
const COPENHAGEN = new Intl.DateTimeFormat('da-DK', {
  timeZone: 'Europe/Copenhagen',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

type CopenhagenParts = { day: string; month: string; year: string; hour: string; minute: string; second: string };

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function copenhagenParts(date: Date): CopenhagenParts {
  const parts = COPENHAGEN.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    day: part('day'),
    month: part('month'),
    year: part('year'),
    hour: part('hour'),
    minute: part('minute'),
    second: part('second'),
  };
}

/** "dd-mm-åååå" in Europe/Copenhagen, or '' for no/invalid value. */
export function formatDanishDate(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  const p = copenhagenParts(date);
  return `${p.day}-${p.month}-${p.year}`;
}

/** "dd-mm-åååå tt:mm" in Europe/Copenhagen, or '' for no/invalid value. */
export function formatDanishDateTime(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  const p = copenhagenParts(date);
  return `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}`;
}

/** "dd-mm-åååå tt:mm:ss" in Europe/Copenhagen — audit/technical timestamps only. */
export function formatDanishDateTimeSeconds(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  const p = copenhagenParts(date);
  return `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}:${p.second}`;
}

/** "tt:mm" in Europe/Copenhagen, or ''. */
export function formatDanishTime(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  const p = copenhagenParts(date);
  return `${p.hour}:${p.minute}`;
}

/**
 * A wall-clock date and optional time as written in a MedCom message
 * ("2026-11-03" / "20261103", "14:32" / "1432") → "dd-mm-åååå tt:mm".
 * No time-zone conversion: the message already carries Danish local time.
 */
export function formatDanishWallClock(date: string | null | undefined, time?: string | null): string {
  const digits = (date ?? '').replace(/[^0-9]/g, '');
  if (digits.length !== 8) return '';
  const shown = `${digits.slice(6, 8)}-${digits.slice(4, 6)}-${digits.slice(0, 4)}`;
  const clock = (time ?? '').replace(/[^0-9]/g, '').slice(0, 4);
  return clock.length === 4 ? `${shown} ${clock.slice(0, 2)}:${clock.slice(2, 4)}` : shown;
}
